import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useColorPalette } from '../../contexts/ColorPaletteContext';
import tvTheme, { tvColLabelColor, tvTextStroke } from './tvTheme';

const SIZES = {
  compact: {
    labelW: 84,
    headerH: 24,
    cellH: 38,
    labelFont: 12,
    headerFont: 12,
    statusFont: 15,
  },
  full: {
    labelW: 176,
    headerH: 46,
    cellH: 96,
    labelFont: 25,
    headerFont: 27,
    statusFont: 35,
  },
};

/**
 * 地点 × 曜日の状態表（日別版の TVHeatmapGrid と同じ枠・帯の作り）
 * セルは状態の文字のみ。欠測は 0 にせず「データなし」
 */
function TVWeeklyStatusGrid({ locations, compact = false }) {
  const { getCellColor, getTextColor } = useColorPalette();
  const size = compact ? SIZES.compact : SIZES.full;
  const days = locations?.[0]?.days || [];

  if (!days.length) return null;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: `${size.labelW}px repeat(${days.length}, minmax(0, 1fr))`,
        border: tvTheme.colors.panelBorder,
        borderRadius: 0.5,
        overflow: 'hidden',
        boxShadow: '0 4px 18px rgba(13,74,143,0.18)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: size.headerH,
          bgcolor: tvTheme.colors.labelBar,
          borderRight: `2px solid ${tvTheme.colors.gridLine}`,
          borderBottom: `2px solid ${tvTheme.colors.gridLine}`,
        }}
      >
        <Typography
          sx={{ fontSize: size.headerFont, fontWeight: 800, color: '#fff', ...tvTextStroke() }}
        >
          地点
        </Typography>
      </Box>
      {days.map((day) => (
        <Box
          key={`head-${day.date}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: size.headerH,
            bgcolor: tvTheme.colors.headerBar,
            borderRight: `2px solid ${tvTheme.colors.gridLine}`,
            borderBottom: `2px solid ${tvTheme.colors.gridLine}`,
          }}
        >
          <Typography
            sx={{
              fontSize: size.headerFont,
              fontWeight: 900,
              color: tvColLabelColor(day.weekday),
              lineHeight: 1.1,
            }}
          >
            {day.weekday}
          </Typography>
        </Box>
      ))}

      {(locations || []).map((loc, ri) => {
        const isLastRow = ri === locations.length - 1;
        return (
          <React.Fragment key={loc.spotId}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.3,
                minHeight: size.cellH,
                bgcolor: tvTheme.colors.labelBar,
                borderRight: `2px solid ${tvTheme.colors.gridLine}`,
                borderBottom: isLastRow ? 'none' : `2px solid ${tvTheme.colors.gridLine}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: size.labelFont,
                  fontWeight: 900,
                  color: '#fff',
                  lineHeight: 1.15,
                  textAlign: 'center',
                  ...tvTextStroke(),
                }}
              >
                {loc.locationName}
              </Typography>
            </Box>
            {loc.days.map((day, ci) => {
              const missing = day.value == null;
              const longLabel = !missing && (day.statusLabel || '').length > 4;
              const statusFont = missing
                ? Math.round(size.statusFont * 0.6)
                : longLabel
                  ? Math.round(size.statusFont * 0.62)
                  : size.statusFont;
              return (
                <Box
                  key={`cell-${loc.spotId}-${day.date}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: longLabel ? 0.2 : 0,
                    minHeight: size.cellH,
                    bgcolor: missing ? '#e8eef3' : getCellColor(day.value),
                    borderRight:
                      ci === loc.days.length - 1 ? 'none' : `2px solid ${tvTheme.colors.gridLine}`,
                    borderBottom: isLastRow ? 'none' : `2px solid ${tvTheme.colors.gridLine}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: statusFont,
                      fontWeight: 900,
                      color: missing ? '#5a6a7a' : getTextColor(day.value),
                      lineHeight: 1.05,
                      textAlign: 'center',
                    }}
                  >
                    {missing ? 'データなし' : day.statusLabel}
                  </Typography>
                </Box>
              );
            })}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

TVWeeklyStatusGrid.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      spotId: PropTypes.string.isRequired,
      locationName: PropTypes.string.isRequired,
      days: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string.isRequired,
          weekday: PropTypes.string.isRequired,
          value: PropTypes.number,
          statusLabel: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }),
  ),
  compact: PropTypes.bool,
};

TVWeeklyStatusGrid.defaultProps = {
  locations: [],
  compact: false,
};

export default TVWeeklyStatusGrid;
